import { useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Plus, Filter, Download, Upload, ListChecks, Clock3, RotateCcw, ArrowRight } from "lucide-react";
import { LeadStatus } from "../types";
import { format, parseISO, isPast, isToday } from "date-fns";
import { AddLeadDialog } from "../components/AddLeadDialog";
import Papa from "papaparse";

const statusColors: Record<LeadStatus, string> = {
  "New Lead": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  "Contacted": "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  "Follow-Up": "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  "Appointment Set": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
  "Offer Made": "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
  "Under Contract": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  "Dead Lead": "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
};

const statusOrder: LeadStatus[] = ["New Lead", "Contacted", "Follow-Up", "Appointment Set", "Offer Made", "Under Contract", "Dead Lead"];

export default function Leads() {
  const { leads } = useAppStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "All">("All");
  const [quickFilter, setQuickFilter] = useState("All");

  const hasDueFollowUp = (lead: (typeof leads)[number]) =>
    lead.followUps.some((fu) => !fu.completed && (isPast(parseISO(fu.dueDate)) || isToday(parseISO(fu.dueDate))));

  const dueTodayFollowUp = (lead: (typeof leads)[number]) =>
    lead.followUps.some((fu) => !fu.completed && isToday(parseISO(fu.dueDate)));

  const overdueFollowUp = (lead: (typeof leads)[number]) =>
    lead.followUps.some((fu) => !fu.completed && isPast(parseISO(fu.dueDate)) && !isToday(parseISO(fu.dueDate)));

  const filteredLeads = leads
    .filter((lead) => {
      const matchesSearch =
        lead.sellerName.toLowerCase().includes(search.toLowerCase()) ||
        lead.propertyAddress.toLowerCase().includes(search.toLowerCase()) ||
        lead.phone.includes(search);
      const matchesStatus = statusFilter === "All" || lead.status === statusFilter;

      let matchesQuick = true;
      if (quickFilter === "Hot") {
        matchesQuick = lead.motivation.toLowerCase().includes("high") || lead.motivation.toLowerCase().includes("must sell");
      } else if (quickFilter === "FollowUpDue") {
        matchesQuick = hasDueFollowUp(lead);
      }

      return matchesSearch && matchesStatus && matchesQuick;
    })
    .sort((a, b) => parseISO(b.createdAt).getTime() - parseISO(a.createdAt).getTime());

  const handleExportCSV = () => {
    const csvData = filteredLeads.map((lead) => ({
      "Seller Name": lead.sellerName,
      "Phone": lead.phone,
      "Property Address": lead.propertyAddress,
      "Status": lead.status,
      "Asking Price": lead.askingPrice || "",
      "Motivation": lead.motivation,
      "Date Added": format(parseISO(lead.createdAt), "yyyy-MM-dd"),
    }));

    const csvStr = Papa.unparse(csvData);
    const blob = new Blob([csvStr], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Leads_Export_${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalLeads = leads.length;
  const newLeadsCount = leads.filter((lead) => lead.status === "New Lead").length;
  const followUpsDueCount = leads.filter((lead) => hasDueFollowUp(lead)).length;
  const dueTodayCount = leads.filter((lead) => dueTodayFollowUp(lead)).length;
  const overdueCount = leads.filter((lead) => overdueFollowUp(lead)).length;
  const followUpStageCount = leads.filter((lead) => lead.status === "Follow-Up").length;
  const isEmpty = totalLeads === 0;
  const hasActiveFilters = search.trim() !== "" || statusFilter !== "All" || quickFilter !== "All";
  const statusBreakdown = statusOrder
    .map((status) => ({ status, count: leads.filter((lead) => lead.status === status).length }))
    .filter((item) => item.count > 0);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setQuickFilter("All");
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-6 px-5 py-6 sm:px-8 lg:grid-cols-[1.2fr_.8fr] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Acquisition</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Lead inbox</span>
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Lead Inbox</h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-500">
                Track sellers, filter by status, and keep the follow-up queue moving without losing context.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-600">
              <span className="rounded-full bg-slate-100 px-3 py-1.5">{totalLeads} total leads</span>
              <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">{followUpsDueCount} follow-ups due</span>
              <span className="rounded-full bg-rose-50 px-3 py-1.5 text-rose-700">{overdueCount} overdue</span>
              <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-700">{newLeadsCount} new leads</span>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-stretch">
            <Button variant="outline" onClick={() => navigate("/import")} className="h-11 justify-start border-slate-200 bg-slate-50 text-slate-700 shadow-sm hover:bg-slate-100 sm:flex-1 lg:flex-none">
              <Upload className="mr-2 h-4 w-4" /> Import CSV
            </Button>
            <Button variant="outline" onClick={handleExportCSV} className="h-11 justify-start border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 sm:flex-1 lg:flex-none">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <AddLeadDialog>
              <Button className="h-11 justify-start bg-slate-950 px-4 font-semibold text-white shadow-sm shadow-slate-950/15 hover:bg-slate-800 sm:flex-1 lg:flex-none">
                <Plus className="mr-2 h-4 w-4" /> Add Lead
              </Button>
            </AddLeadDialog>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <ListChecks className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Total leads</p>
              <p className="text-2xl font-bold text-slate-900">{totalLeads}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <Clock3 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">New leads</p>
              <p className="text-2xl font-bold text-slate-900">{newLeadsCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <Clock3 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-slate-400">Follow-ups due</p>
              <p className="text-2xl font-bold text-slate-900">{followUpsDueCount}</p>
              <p className="text-xs text-slate-500">{dueTodayCount} due today</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <Clock3 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-slate-400">Overdue now</p>
              <p className="text-2xl font-bold text-slate-900">{overdueCount}</p>
              <p className="text-xs text-slate-500">Needs attention first</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">At-a-glance breakdown</p>
              <p className="text-sm text-slate-500">Status mix and follow-up pressure, so you can prioritize fast.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {statusBreakdown.slice(0, 3).map(({ status, count }) => (
                <span key={status} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
                  <span className={`h-2 w-2 rounded-full ${statusColors[status].split(" ")[0]}`} />
                  {status}
                  <span className="rounded-full bg-white px-2 py-0.5 text-slate-600">{count}</span>
                </span>
              ))}
              {followUpStageCount > 0 && (
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800">
                  <Clock3 className="h-3.5 w-3.5" /> Follow-Up <span className="rounded-full bg-white px-2 py-0.5 text-blue-700">{followUpStageCount}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
                <Clock3 className="h-3.5 w-3.5" /> Due today <span className="rounded-full bg-white px-2 py-0.5 text-amber-700">{dueTodayCount}</span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-800">
                <Clock3 className="h-3.5 w-3.5" /> Overdue <span className="rounded-full bg-white px-2 py-0.5 text-rose-700">{overdueCount}</span>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {isEmpty ? (
        <Card className="border-dashed border-slate-200 bg-white shadow-sm">
          <CardContent className="flex flex-col items-center gap-5 p-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <ListChecks className="h-7 w-7" />
            </div>
            <div className="space-y-2 max-w-xl">
              <h2 className="text-lg font-semibold text-slate-900">Your lead inbox is empty</h2>
              <p className="text-sm text-slate-500">
                Start with a CSV import or add a single lead manually. Once you have leads, you can filter, export, and work follow-ups from here.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => navigate("/import")} className="bg-blue-600 hover:bg-blue-700">
                <Upload className="mr-2 h-4 w-4" /> Import CSV
              </Button>
              <AddLeadDialog>
                <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                  <Plus className="mr-2 h-4 w-4" /> Add Lead
                </Button>
              </AddLeadDialog>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative w-full sm:min-w-[220px] sm:max-w-sm sm:flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search name, phone, or address..."
                className="pl-9 border-slate-200 focus-visible:ring-blue-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-[180px]">
              <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as LeadStatus | "All")}> 
                <SelectTrigger className="border-slate-200 bg-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Statuses</SelectItem>
                  <SelectItem value="New Lead">New Lead</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Follow-Up">Follow-Up</SelectItem>
                  <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                  <SelectItem value="Offer Made">Offer Made</SelectItem>
                  <SelectItem value="Under Contract">Under Contract</SelectItem>
                  <SelectItem value="Dead Lead">Dead Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-[180px]">
              <Select value={quickFilter} onValueChange={setQuickFilter}>
                <SelectTrigger className="border-slate-200 bg-white">
                  <div className="flex items-center gap-2">
                    <Filter className="w-3 h-3 text-slate-400" />
                    <SelectValue placeholder="Quick Filter" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">No Quick Filter</SelectItem>
                  <SelectItem value="Hot">Hot Leads (High Motivation)</SelectItem>
                  <SelectItem value="FollowUpDue">Follow-up Due / Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:justify-end">
              <p className="text-sm text-slate-500">
                Showing <span className="font-medium text-slate-800">{filteredLeads.length}</span> of <span className="font-medium text-slate-800">{totalLeads}</span> leads
              </p>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-600 hover:bg-slate-100 hover:text-slate-900">
                  <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reset filters
                </Button>
              )}
            </div>
          </div>

          {filteredLeads.length === 0 ? (
            <Card className="border-dashed border-slate-200 bg-white shadow-sm">
              <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <Search className="h-6 w-6" />
                </div>
                <div className="max-w-xl space-y-2">
                  <h2 className="text-lg font-semibold text-slate-900">No leads match these filters</h2>
                  <p className="text-sm text-slate-500">
                    Try clearing the search or filters to widen the list. If you’re starting fresh, you can import a CSV or add a lead manually.
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button variant="outline" onClick={resetFilters} className="border-slate-200 text-slate-700 hover:bg-slate-50">
                    <RotateCcw className="mr-2 h-4 w-4" /> Clear filters
                  </Button>
                  <Button onClick={() => navigate("/import")} className="bg-blue-600 hover:bg-blue-700">
                    <Upload className="mr-2 h-4 w-4" /> Import CSV
                  </Button>
                  <AddLeadDialog>
                    <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                      <Plus className="mr-2 h-4 w-4" /> Add Lead
                    </Button>
                  </AddLeadDialog>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-3 md:hidden">
                {filteredLeads.map((lead) => {
                  const dueToday = dueTodayFollowUp(lead);
                  const overdue = overdueFollowUp(lead);

                  return (
                    <Card key={lead.id} className="border-slate-200 shadow-sm transition-shadow hover:shadow-md">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{lead.sellerName}</p>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{lead.phone || "No Phone"}</p>
                          </div>
                          <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${statusColors[lead.status]}`}>
                            {lead.status}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-sm text-slate-600">{lead.propertyAddress}</p>
                          <p className="text-sm text-slate-500 line-clamp-2">{lead.motivation || "-"}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span>Added {format(parseISO(lead.createdAt), "MMM d, yyyy")}</span>
                            {dueToday && <span className="rounded-full bg-amber-50 px-2 py-1 font-medium text-amber-700">Follow-up today</span>}
                            {overdue && <span className="rounded-full bg-rose-50 px-2 py-1 font-medium text-rose-700">Follow-up overdue</span>}
                          </div>
                        </div>
                        <Button variant="outline" onClick={() => navigate(`/leads/${lead.id}`)} className="w-full justify-between border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-semibold">
                          <span>View lead</span>
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="hidden md:block rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold text-slate-600">Seller</TableHead>
                      <TableHead className="font-semibold text-slate-600">Property</TableHead>
                      <TableHead className="font-semibold text-slate-600">Status</TableHead>
                      <TableHead className="font-semibold text-slate-600">Motivation</TableHead>
                      <TableHead className="font-semibold text-slate-600">Added</TableHead>
                      <TableHead className="text-right font-semibold text-slate-600">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow key={lead.id} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium text-slate-800">
                          {lead.sellerName}
                          <div className="text-xs text-slate-500 font-normal mt-0.5">{lead.phone || "No Phone"}</div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-slate-600">{lead.propertyAddress}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[lead.status]}`}>
                            {lead.status}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">
                          {lead.motivation || "-"}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">{format(parseISO(lead.createdAt), "MMM d, yyyy")}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${lead.id}`)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-semibold px-3">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
